/**
 * grass_params.ts — the grass layer's rings per tier (2026-09-25, visual-realism R2). Pure.
 *
 * The layer is a set of RINGS around the camera, each one fixed-count instanced draw on a
 * WORLD-LOCKED lattice that snaps with the camera (a blade belongs to a lattice cell, never to an
 * instance slot — so nothing swims as you walk):
 *   · near blades — 4 segments (7 tris), the densest lattice;
 *   · mid blades  — 2 segments (3 tris), half the lattice density per axis and wider blades;
 *   · clump cards — one camera-yawed quad of procedural blades (2 tris), out to ~57 m.
 * Neighbouring rings share a FADE BAND: the inner ring's keep weight falls with the same smoothstep
 * the outer one rises with, so the coverage is continuous across every boundary.
 *
 * LATTICE LAW: every spacing divides 1 m exactly (1/k). The render anchor moves in whole metres, so
 * the anchor's offset in cells is an INTEGER and a blade's hash (and so its jitter, height, yaw) is
 * the same before and after a rebase — the blades do not reshuffle every 10 km.
 */

export type GrassLayerTier = 'off' | 'cards' | 'low' | 'med' | 'high' | 'ultra';
export const GRASS_LAYER_TIERS: readonly GrassLayerTier[] = ['off', 'cards', 'low', 'med', 'high', 'ultra'];

export interface GrassRing {
  kind: 'blade' | 'card';
  /** Instances per axis (the draw is grid² instances). */
  grid: number;
  /** Lattice cells per metre (spacing = 1 / cellsPerM — an integer, the lattice law). */
  cellsPerM: number;
  /** Blade segments (blades only): 4 → 9 verts / 7 tris, 2 → 5 verts / 3 tris. */
  segments: 2 | 4;
  /** Radial band (m) this ring owns, and its fade widths at each edge. */
  rIn: number; rOut: number; fadeIn: number; fadeOut: number;
  /** Base width of a blade (m), or the card's width as a multiple of its height. */
  width: number;
  /** Keep probability per cell at full density (cards thin to 1–3 per m²). */
  keep: number;
}
export interface GrassParams { tier: GrassLayerTier; rings: GrassRing[] }

const blade = (grid: number, cellsPerM: number, segments: 2 | 4, rIn: number, rOut: number, fadeIn: number, fadeOut: number, width: number): GrassRing =>
  ({ kind: 'blade', grid, cellsPerM, segments, rIn, rOut, fadeIn, fadeOut, width, keep: 1 });
const card = (grid: number, cellsPerM: number, rIn: number, rOut: number, fadeIn: number, fadeOut: number, keep: number): GrassRing =>
  ({ kind: 'card', grid, cellsPerM, segments: 2, rIn, rOut, fadeIn, fadeOut, width: 1.25, keep });

/**
 * The tier table. `rOut` never exceeds the lattice's half size less one cell (a camera anywhere in
 * its base cell still covers the ring), and the cards stay inside the field window (±60 m from the
 * camera — lib/grass/grass_field.ts).
 *   high  ≈ 59 k blades within 24.5 m (36,864 near + 22,500 mid) + 53.8 k card cells to 57 m
 *   ultra ≈ 79 k blades within 29.5 m (×1.25 the density of high by area)
 *   med   ≈ 40 k blades within 19.5 m, cards to 48 m
 *   low   = 16,384 blades within 12.5 m (2 segments), cards to 35 m
 *   cards = cards only to 27 m (potato)
 *   off   = nothing (minimal)
 */
export const GRASS_TIERS: Record<GrassLayerTier, GrassParams> = {
  off: { tier: 'off', rings: [] },
  cards: { tier: 'cards', rings: [card(112, 2, 0, 27, 0, 5, 0.42)] },
  low: { tier: 'low', rings: [blade(128, 5, 2, 0, 12.5, 0, 2.5, 0.05), card(144, 2, 12.5, 35, 2.5, 6, 0.45)] },
  med: { tier: 'med', rings: [blade(160, 6, 4, 0, 13, 0, 3, 0.04), blade(120, 3, 2, 13, 19.5, 3, 3.5, 0.07), card(196, 2, 19.5, 48, 3.5, 7, 0.5)] },
  high: { tier: 'high', rings: [blade(192, 6, 4, 0, 15.5, 0, 3, 0.04), blade(150, 3, 2, 15.5, 24.5, 3, 3.5, 0.07), card(232, 2, 24.5, 57, 3.5, 8, 0.5)] },
  ultra: { tier: 'ultra', rings: [blade(216, 6, 4, 0, 17.5, 0, 3, 0.04), blade(180, 3, 2, 17.5, 29.5, 3, 4, 0.07), card(232, 2, 29.5, 57, 4, 8, 0.55)] },
};

export function grassParamsFor(tier: GrassLayerTier | undefined): GrassParams { return GRASS_TIERS[tier ?? 'off'] ?? GRASS_TIERS.off; }

/** Vertices / triangles of one instance of a ring's template. */
export function ringTemplate(r: GrassRing): { verts: number; tris: number } {
  if (r.kind === 'card') return { verts: 4, tris: 2 };
  return r.segments === 4 ? { verts: 9, tris: 7 } : { verts: 5, tris: 3 };
}

/** Instances, blades, cards and the worst-case triangle count (every instance kept) of a tier. */
export function grassCounts(p: GrassParams): { instances: number; blades: number; cards: number; maxTris: number; bladeReach: number; cardReach: number } {
  let blades = 0, cards = 0, maxTris = 0, bladeReach = 0, cardReach = 0;
  for (const r of p.rings) {
    const n = r.grid * r.grid;
    maxTris += n * ringTemplate(r).tris;
    if (r.kind === 'blade') { blades += n; bladeReach = Math.max(bladeReach, r.rOut); } else { cards += n; cardReach = Math.max(cardReach, r.rOut); }
  }
  return { instances: blades + cards, blades, cards, maxTris, bladeReach, cardReach };
}

/**
 * Expected triangles actually drawn at full density (the fraction of each ring's lattice that lies
 * in its band, times its keep): what the census should see over an open meadow.
 */
export function grassExpectedTris(p: GrassParams, density = 1): number {
  let t = 0;
  for (const r of p.rings) {
    const cellArea = 1 / (r.cellsPerM * r.cellsPerM);
    const band = Math.PI * (r.rOut * r.rOut - r.rIn * r.rIn);
    t += (band / cellArea) * r.keep * density * ringTemplate(r).tris;
  }
  return Math.round(t);
}

/** The radial keep weight of a ring at distance `d` from the camera (the shader's band). */
export function ringWeight(r: GrassRing, d: number): number {
  const ss = (a: number, b: number, x: number): number => { if (b <= a) return x >= b ? 1 : 0; const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
  const inW = r.rIn <= 0 ? 1 : ss(r.rIn - r.fadeIn, r.rIn, d);
  const outW = 1 - ss(r.rOut - r.fadeOut, r.rOut, d);
  return inW * outW;
}
